import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {Button} from '@common-web/components/ui/button';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import {PayrollFileColumnMapping} from '@api/lib/payrollProcessing/types';
import {ExtractedFile} from '@client/pages/payroll/types';
import {COLUMN_TRANSFORMATION_OPTIONS} from '@client/pages/payroll/constants/transformationOptions';
import {Database, Zap, Layers} from 'lucide-react';
import {toTitleCase} from '@common/lib/utils';
import {memo} from 'react';

type ConfiguredColumnPopoverProps = {
  mapping: PayrollFileColumnMapping;
  extractedFiles: ExtractedFile[];
  children: React.ReactNode;
};

export const ConfiguredColumnPopover = memo(
  ({mapping, extractedFiles, children}: ConfiguredColumnPopoverProps) => {
    const transformation = mapping.columnTransformation;

    const getColumnDetails = () => {
      if (transformation.type === 'STATIC_VALUE') {
        return [];
      }
      if (transformation.type === 'NONE') {
        const col = transformation.column;
        const file = extractedFiles.find((f) => f.file.fileId === col.fileId);
        const column = file?.columns.find((c) => c.name === col.name && c.index === col.index);
        return [
          {
            fileId: col.fileId,
            fileName: file?.file.fileName,
            columnName: col.name,
            samples: column?.samples || [],
          },
        ];
      }

      if (transformation.type === 'PIVOT_COLUMN') {
        const pivotCol = transformation.pivotColumn;
        const file = extractedFiles.find((f) => f.file.fileId === pivotCol.fileId);
        const pivotColumn = file?.pivotColumns?.find(
          (c) => c.pivotKeyValue === pivotCol.pivotKeyValue,
        );
        return [
          {
            fileId: pivotCol.fileId,
            fileName: file?.file.fileName,
            columnName: pivotCol.name,
            samples: pivotColumn?.samples || [],
            isPivot: true,
          },
        ];
      }

      if ('columns' in transformation) {
        return transformation.columns.map((col) => {
          const file = extractedFiles.find((f) => f.file.fileId === col.fileId);
          const column = file?.columns.find((c) => c.name === col.name && c.index === col.index);
          return {
            fileId: col.fileId,
            fileName: file?.file.fileName,
            columnName: col.name,
            samples: column?.samples || [],
          };
        });
      }

      return [];
    };

    const columnDetails = getColumnDetails();
    const transformationLabel =
      COLUMN_TRANSFORMATION_OPTIONS.find((opt) => opt.type === transformation.type)?.label ||
      toTitleCase(transformation.type.replace(/_/g, ' '));
    const isMultiColumn =
      transformation.type !== 'NONE' &&
      transformation.type !== 'PIVOT_COLUMN' &&
      transformation.type !== 'STATIC_VALUE';

    return (
      <Popover>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent className='w-80 p-0' side='bottom' align='start'>
          <div className='border-b border-gray-100 bg-gray-50 px-4 py-3'>
            <div className='flex items-center gap-2 text-sm font-medium text-internal-brand-navy'>
              {isMultiColumn ? (
                <>
                  <Layers className='h-4 w-4 text-emerald' />
                  {transformationLabel}
                </>
              ) : (
                <>
                  <Database className='h-4 w-4 text-internal-brand-blue' />
                  Column Details
                </>
              )}
            </div>
          </div>
          <div className='max-h-72 overflow-y-auto p-3 space-y-3'>
            {transformation.type === 'STATIC_VALUE' && (
              <div className='rounded-lg border border-gray-200 bg-white overflow-hidden'>
                <div className='bg-gray-50 px-3 py-2 border-b border-gray-100'>
                  <span className='font-medium text-sm text-internal-brand-navy'>Static Value</span>
                </div>
                <div className='px-3 py-2'>
                  <div className='text-sm font-mono bg-gray-50 border border-gray-100 rounded px-2 py-1'>
                    {transformation.staticValue}
                  </div>
                </div>
              </div>
            )}
            {columnDetails.map((detail, idx) => (
              <div
                key={`${detail.fileId}-${detail.columnName}-${idx}`}
                className='rounded-lg border border-gray-200 bg-white overflow-hidden'
              >
                <div className='bg-gray-50 px-3 py-2 border-b border-gray-100'>
                  <div className='flex items-center justify-between gap-2 flex-wrap'>
                    <span className='font-medium text-sm text-internal-brand-navy'>
                      {detail.columnName}
                    </span>
                    <FileIdBadge fileId={detail.fileId} className='text-xs py-0.5 px-2' />
                  </div>
                </div>
                <div className='px-3 py-2'>
                  <div className='text-xs font-medium text-gray-500 mb-1.5'>Sample Values</div>
                  {detail.samples && detail.samples.length > 0 ? (
                    <div className='space-y-1'>
                      {detail.samples.slice(0, 5).map((sample, sampleIdx) => (
                        <div
                          key={sampleIdx}
                          className='text-sm font-mono bg-gray-50 border border-gray-100 rounded px-2 py-1 truncate'
                          title={sample}
                        >
                          {sample || <span className='text-gray-400 italic'>(empty)</span>}
                        </div>
                      ))}
                      {detail.samples.length > 5 && (
                        <div className='text-xs text-gray-400 italic pt-1'>
                          + {detail.samples.length - 5} more values
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className='text-sm text-gray-400 italic'>No sample data available</div>
                  )}
                </div>
              </div>
            ))}
            {mapping.exampleValuePreTransformation && (
              <div className='rounded-lg border border-emerald/30 bg-emerald/5 px-3 py-2 space-y-2'>
                <div className='flex items-center gap-1.5 text-xs font-medium text-emerald'>
                  <Zap className='h-3.5 w-3.5' />
                  Transformation Preview
                </div>
                <div className='grid grid-cols-2 gap-2 text-xs'>
                  <div>
                    <div className='text-gray-500 mb-0.5'>Before</div>
                    <div
                      className='font-mono bg-white border border-gray-200 rounded px-2 py-1 truncate'
                      title={mapping.exampleValuePreTransformation}
                    >
                      {mapping.exampleValuePreTransformation || (
                        <span className='text-gray-400 italic'>(empty)</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className='text-gray-500 mb-0.5'>After</div>
                    <div
                      className='font-mono bg-white border border-emerald/30 rounded px-2 py-1 truncate'
                      title={mapping.exampleValuePostTransformation}
                    >
                      {mapping.exampleValuePostTransformation || (
                        <span className='text-gray-400 italic'>(empty)</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);
